import { createContext, useEffect, useState } from "react";
import {
  BROADBAND_INFRASTRUCTURES,
  CELL_TOWERS,
  DRC,
  FIBRE_OPTIC_LINES,
} from "./utils/strings";
import { checkExistingData } from "./utils/api";
import useCountryData from "./custom-hooks/country-data";
import useTheme from "./custom-hooks/theme";
import useItuData from "./custom-hooks/itu-data";
import useAffordabilityLegends from "./custom-hooks/affordability-legends";

export const Context = createContext(null);

const initialSelectedDatasets = [BROADBAND_INFRASTRUCTURES, FIBRE_OPTIC_LINES, CELL_TOWERS];

const ContextProvider = ({ children }) => {
  const [map, setMap] = useState(null);
  const [isShownPopulationInfo, setIsShownPopulationInfo] = useState(false);
  const [indicatorData, setIndicatorData] = useState({
    layerLevel: "",
    year: "",
    indicator: "",
    urbanization: "",
    gender: "",
    age: "",
    affordability: "",
    affordabilityCategory: "",
  });
  const [selectedDatasets, setSelectedDatasets] = useState(initialSelectedDatasets);
  const [loading, setLoading] = useState([]);
  const [isShownModal, setIsShownModal] = useState(false);
  const [isOpenedDrawer, setIsOpenedDrawer] = useState(false);
  const [message, setMessage] = useState("");
  const [position, setPosition] = useState({ lat: -19.3, lon: 34.91 });
  const [existingData, setExistingData] = useState([]);
  const [cellTowerRange, setCellTowerRange] = useState([0, 1000]);
  const [ituDataList, setItuDataList] = useState([]);
  const [cacheCleaner, setCacheCleaner] = useState(false);
  const [isLoadPdf, setIsLoadPdf] = useState(false);

  // Retrieve items from session storage
  const theme = JSON.parse(sessionStorage.getItem("theme"));

  const { darkMode, setDarkMode } = useTheme({ theme });
  const { countries, countryName, setCountryName } = useCountryData({ setMessage });

  const newCountryName = countryName === DRC ? "drc" : countryName;

  const sliderDefaultLayer = ituDataList?.includes(`${newCountryName}_itu_district`) ? "district" : "province";

  const [sliderData, setSliderData] = useState({
    year: "2020",
    layerLevel: sliderDefaultLayer,
    activeMobile: [5, 30],
    internetUsers: [0.1, 0.3],
    anySpeed: [0.28, 0.56],
    cellTowers: [2261, 4662],
  });

  const [existingLegends, setExistingLegends] = useState([]);
  const [affordabilitySelection, setAffordabilitySelection] = useState([]);
  const { indicator, layerLevel, year } = indicatorData;

  const affordAdmin = layerLevel === "province" ? "provinces" : "district";

  const layerLevelPerCountry = isOpenedDrawer ? sliderData.layerLevel : layerLevel;

  const { ituLegendData, ituLegends, getItuLegends, getITUData } = useItuData({
    existingData,
    indicator,
    countryName,
    newCountryName,
    existingLegends,
    layerLevelPerCountry,
    setLoading,
    setMessage,
    setExistingData,
    setExistingLegends
  });

  const { affordabilityLegends } = useAffordabilityLegends({
    indicatorData,
    existingLegends,
    newCountryName,
    affordAdmin
  });

  const legends =
    affordabilitySelection.length > 0 && !isOpenedDrawer
      ? affordabilityLegends
      : ituLegends;

  useEffect(() => {
    if ((!ituLegendData && year) || isOpenedDrawer) {
      getItuLegends(layerLevelPerCountry);
    };

    if ((layerLevel && year && indicator && affordabilitySelection.length === 0) || isOpenedDrawer) {
      getITUData(layerLevelPerCountry);
    };
    // Bypass linting as the required dependency cause unnecessary re-rendering
    // eslint-disable-next-line
  }, [
    year,
    indicator,
    layerLevel,
    isOpenedDrawer,
    sliderData.layerLevel,
    affordabilitySelection.length,
  ]);

  useEffect(() => {
    setDarkMode(theme);
    checkExistingData(countryName, setMessage, setItuDataList);
    // Bypass linting as the required dependency cause unnecessary re-rendering
    // eslint-disable-next-line
  }, []);

  return (
    <Context.Provider
      value={{
        // Map props
        map,
        setMap,
        darkMode,
        setDarkMode,
        position,
        setPosition,

        // Modals props
        isShownPopulationInfo,
        setIsShownPopulationInfo,
        isShownModal,
        setIsShownModal,

        // Drawers
        isOpenedDrawer,
        setIsOpenedDrawer,
        sliderData,
        setSliderData,

        // Countries
        countries,
        countryName,
        setCountryName,

        // Indicators
        indicatorData,
        setIndicatorData,
        selectedDatasets,
        setSelectedDatasets,
        existingData,
        setExistingData,
        cellTowerRange,
        setCellTowerRange,
        ituLegends,
        legends,
        existingLegends,
        affordabilityLegends,
        setExistingLegends,
        ituDataList,
        setItuDataList,
        affordabilitySelection,
        setAffordabilitySelection,

        // Misc
        cacheCleaner,
        setCacheCleaner,
        loading,
        setLoading,
        message,
        setMessage,
        isLoadPdf,
        setIsLoadPdf
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default ContextProvider;
